const isProduction = import.meta.env.VITE_ENV === 'production';

const safeLog = (actionName, payload) => {
  if (isProduction) { return; }
  try {
    // eslint-disable-next-line no-console
    console.log('[DD RUM]', actionName, payload);
  } catch {
    // ignore console failures
  }
};

export const setGlobalContextProperty = (key, payload) => {
  try {
    window.DD_RUM?.setGlobalContextProperty(key, payload);
  } catch {
    // fail silent
  }
};

export const readGlobalContextProperty = (key) => {
  try {
    return window.DD_RUM?.getGlobalContext?.()?.[key];
  } catch {
    return null;
  }
};

export const removeGlobalContextProperty = (key) => {
  try {
    window.DD_RUM?.removeGlobalContextProperty(key);
  } catch {
    // fail silent
  }
};

export const setViewContextProperty = (key, value) => {
  try {
    window.DD_RUM?.setViewContextProperty(key, value);
  } catch {
    // fail silent
  }
};

export const setUser = (payload = {}) => {
  try {
    window.DD_RUM?.setUser(payload);
  } catch {
    // fail silent
  }
};

export const addAction = (actionName = '', payload = {}) => {
  try {
    safeLog(actionName, payload);
    window.DD_RUM?.addAction(actionName, payload);
  } catch {
    // fail silent
  }
};

export const addFeatureFlagEvaluation = (featureName = '', value = true) => {
  try {
    window.DD_RUM?.addFeatureFlagEvaluation(featureName, value);
  } catch {
    // fail silent
  }
};

export const startView = ({ name }) => {
  try {
    window.DD_RUM?.startView?.({ name });
  } catch {
    // fail silent
  }
};

// -----------------------------------------------------------------------------
// Flow tracking (checkout)
// -----------------------------------------------------------------------------

export const DD_ACTIONS = Object.freeze({
  FLOW_STARTED: 'checkout.flow_started',
  STEP_COMPLETED: 'checkout.step_completed',
  FLOW_EXITED: 'checkout.flow_exited',
  PAYMENT_COMPLETED: 'checkout.payment_completed',
  STATUS_VIEWED: 'checkout.status_viewed',
});

export const CHECKOUT_STEPS = Object.freeze({
  ORDER_PLACED: 'order_placed',
  OTP_VERIFIED: 'otp_verified',
  IDENTITY_COMPLETED: 'identity_completed',
  PAYMENT_COMPLETED: 'payment_completed',
  STATUS_VIEWED: 'status_viewed',
});

const CHECKOUT_FLOW_KEY = 'dd.checkout.flow';

const uuid = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
};

const sanitize = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  const out = { ...obj };
  delete out.email;
  delete out.phone;
  delete out.first_name;
  delete out.last_name;
  delete out.full_name;
  delete out.address;
  return out;
};

const detectPlatform = () => {
  if (typeof window !== 'undefined' && window?.Capacitor) return 'mobile';
  return 'web';
};

export const initCheckoutFlow = ({
  entryPoint = 'unknown',
  flowVersion = 'unknown',
  paymentMethod = 'unknown',
  partner = 'direct',
  platform,
  extra = {},
} = {}) => {
  try {
    const flow = sanitize({
      flow_id: uuid(),
      flow_name: 'checkout',
      flow_version: flowVersion,
      entry_point: entryPoint,
      platform: platform || detectPlatform(),
      payment_method: paymentMethod,
      partner,
      ...extra,
    });

    try {
      sessionStorage.setItem(CHECKOUT_FLOW_KEY, JSON.stringify(flow));
    } catch {
      // ignore storage failures
    }

    setGlobalContextProperty('flow', flow);
    addAction(DD_ACTIONS.FLOW_STARTED, flow);

    return flow;
  } catch {
    return null;
  }
};

export const getCheckoutFlow = () => {
  try {
    const ctxFlow = readGlobalContextProperty('flow');
    if (ctxFlow && ctxFlow.flow_id) return ctxFlow;

    const raw = sessionStorage.getItem(CHECKOUT_FLOW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.flow_id ? parsed : null;
  } catch {
    return null;
  }
};

// Dedupe: only send each step once per flow (flow_id:step_name)
const sentStepKeys = new Set();

const stepKey = (flowId, stepName) => (flowId && stepName ? `${flowId}:${stepName}` : null);

export const clearCheckoutFlow = () => {
  try {
    const flow = getCheckoutFlow();
    if (flow?.flow_id) {
      [...sentStepKeys].forEach((key) => {
        if (key.startsWith(`${flow.flow_id}:`)) {
          sentStepKeys.delete(key);
        }
      });
    }
    sessionStorage.removeItem(CHECKOUT_FLOW_KEY);
  } catch {
    // ignore
  }
  removeGlobalContextProperty('flow');
};

export const trackCheckoutStep = ({
  stepName,
  stepIndex,
  extra = {},
} = {}) => {
  try {
    const flow = getCheckoutFlow() || {};
    const key = stepKey(flow?.flow_id, stepName);
    if (key && sentStepKeys.has(key)) {
      return;
    }
    if (key) {
      sentStepKeys.add(key);
    }
    const payload = sanitize({
      ...flow,
      step_name: stepName,
      step_index: stepIndex,
      ...extra,
    });
    safeLog(DD_ACTIONS.STEP_COMPLETED, payload);
    addAction(DD_ACTIONS.STEP_COMPLETED, payload);
  } catch {
    // fail silent
  }
};

export const trackOrderPlaced = (extra = {}) => {
  try {
    trackCheckoutStep({
      stepName: CHECKOUT_STEPS.ORDER_PLACED,
      stepIndex: 1,
      extra,
    });
  } catch {
    // fail silent
  }
};

export const trackOtpVerified = (extra = {}) => {
  try {
    trackCheckoutStep({
      stepName: CHECKOUT_STEPS.OTP_VERIFIED,
      stepIndex: 2,
      extra,
    });
  } catch {
    // fail silent
  }
};

export const trackIdentityCompleted = (extra = {}) => {
  try {
    trackCheckoutStep({
      stepName: CHECKOUT_STEPS.IDENTITY_COMPLETED,
      stepIndex: 3,
      extra,
    });
  } catch {
    // fail silent
  }
};

export const trackPaymentCompleted = (extra = {}) => {
  try {
    const flow = getCheckoutFlow() || {};
    const payload = sanitize({
      ...flow,
      step_name: CHECKOUT_STEPS.PAYMENT_COMPLETED,
      step_index: 4,
      ...extra,
    });
    safeLog(DD_ACTIONS.PAYMENT_COMPLETED, payload);
    addAction(DD_ACTIONS.PAYMENT_COMPLETED, payload);
    clearCheckoutFlow();
  } catch {
    // fail silent
  }
};

export const trackStatusViewed = (extra = {}) => {
  try {
    const flow = getCheckoutFlow() || {};
    const payload = sanitize({
      ...flow,
      step_name: CHECKOUT_STEPS.STATUS_VIEWED,
      step_index: 5,
      ...extra,
    });
    safeLog(DD_ACTIONS.STATUS_VIEWED, payload);
    addAction(DD_ACTIONS.STATUS_VIEWED, payload);
  } catch {
    // fail silent
  }
};

export const trackFlowExit = (exitReason, extra = {}) => {
  try {
    const flow = getCheckoutFlow() || {};
    const payload = sanitize({
      ...flow,
      exit_reason: exitReason ?? 'unknown',
      ...extra,
    });
    safeLog(DD_ACTIONS.FLOW_EXITED, payload);
    addAction(DD_ACTIONS.FLOW_EXITED, payload);
    clearCheckoutFlow();
  } catch {
    // fail silent
  }
};

export const setExperimentVariant = (experimentName, variant) => {
  try {
    addFeatureFlagEvaluation(experimentName, variant);

    const flow = getCheckoutFlow();
    if (flow) {
      const nextFlow = {
        ...flow,
        experiment: { ...(flow.experiment || {}), [experimentName]: variant },
      };
      try {
        sessionStorage.setItem(CHECKOUT_FLOW_KEY, JSON.stringify(nextFlow));
      } catch {
        // ignore
      }
      setGlobalContextProperty('flow', nextFlow);
    }
  } catch {
    // fail silent
  }
};
